import './CoralSocialProof.scss'

import { useIsWindowBiggerThan, useStickyBarHeight } from '@bob/library'
import { Markdown } from '@websrc/App/Components/Markdown/Markdown'
import { mapCoralWebSocialProof } from '@websrc/DataMappers/mapSpot'
import React, { FC, useRef } from 'react'

import { Leaf } from './illustration/Leaf'
import { Line } from './illustration/Line'
import { Potion } from './illustration/Potion'

type CoralWebSocialProofProps = ReturnType<typeof mapCoralWebSocialProof> & {
  renderCoralSpotSpacing?: boolean
}

export const CoralSocialProof: FC<CoralWebSocialProofProps> = ({
  headline,
  description,
  big_numbers,
  renderCoralSpotSpacing,
}) => {
  const isTablet = useIsWindowBiggerThan(719)
  const isDesktop = useIsWindowBiggerThan(1079)
  const totalStickyHeight = useStickyBarHeight()
  const containerRef = useRef<HTMLDivElement>(null)
  const mainTextRef = useRef<HTMLDivElement>(null)
  const lastBigNumberRef = useRef<HTMLDivElement>(null)

  // Function to generate a random pause duration between 1 and 8 seconds
  const getRandomPauseDuration = () => {
    return Math.random() * (6 - 1) + 1
  }

  return (
    <div
      className={`container-coral-spot ${
        renderCoralSpotSpacing ? '' : 'm--top-spacing-100 m--bottom-spacing-100'
      }`}
    >
      <div className={'coral-social-proof'} ref={containerRef}>
        {/** main text */}
        <div
          className={'coral-social-proof__left-container'}
          style={{
            top: `${totalStickyHeight + 60}px`,
          }}
        >
          <div className={'coral-social-proof__main-text'} ref={mainTextRef}>
            <h2 className={'coral-social-proof__header'}>{headline}</h2>
            <Markdown
              className={'coral-social-proof__description'}
              markdown={description ?? ''}
              skipWidth={true}
            />
          </div>
        </div>
        <div className={'coral-social-proof__right-container'}>
          <div className={'coral-social-proof__illustration-container'}>
            {/** big numbers */}
            {big_numbers?.map((item, index) => (
              <div
                className={'coral-social-proof__big-number'}
                key={item.value}
                ref={index === big_numbers?.length - 1 ? lastBigNumberRef : null}
              >
                <h3 className={'coral-social-proof__big-number-value'}>{item.value}</h3>
                <Markdown
                  className={'coral-social-proof__big-number-description'}
                  markdown={item.description ?? ''}
                />
              </div>
            ))}
            {/** svg illustration/animation */}
            {isTablet && (
              <>
                <Line className={'coral-social-proof__line-middle'} />
                <Leaf
                  className={'coral-social-proof__leaf'}
                  pauseDuration={getRandomPauseDuration()}
                  style={{
                    gridColumn: '8 / 9',
                    gridRow: '4 / 5',
                    marginTop: '60px',
                  }}
                />
                <Line
                  className={'coral-social-proof__line-right'}
                  orientation={'horizontal'}
                  position={'end'}
                  style={{ transform: 'rotate(180deg)' }}
                />
                <Line className={'coral-social-proof__line-right'} position={'end'} />
              </>
            )}
            {isDesktop && (
              <Line className={'coral-social-proof__line-left'} orientation={'horizontal'} />
            )}
            <Line
              className={'coral-social-proof__line-left'}
              style={{ height: 'calc(100% - 20px)', justifySelf: isDesktop ? 'start' : 'center' }}
            />
            <Leaf
              className={'coral-social-proof__leaf m--bottom-left'}
              pauseDuration={getRandomPauseDuration()}
              style={{
                gridColumn: isTablet && !isDesktop ? '5 / 6' : isDesktop ? '2 / 3' : '1 / 2',
                gridRow: isTablet ? '8 / 9' : ' 6 / 7',
                alignSelf: 'center',
                justifySelf: isDesktop ? 'start' : 'center',
                marginLeft: isDesktop ? '1px' : '23px',
                marginBottom: isDesktop ? '15px' : '55px',
              }}
            />
            <Leaf
              className={'coral-social-proof__leaf m--bottom-right'}
              direction={'left'}
              pauseDuration={getRandomPauseDuration()}
              style={{
                gridColumn: isTablet && !isDesktop ? '5 / 6' : '1 / 2',
                gridRow: isTablet ? '8 / 9' : ' 6 / 7',
                alignSelf: 'center',
                justifySelf: isDesktop ? 'end' : 'center',
                marginRight: isDesktop ? '3px' : '33px',
                marginTop: isDesktop ? '40px' : '0px',
              }}
            />
            <Potion className={'coral-social-proof__potion'} />
          </div>
        </div>
      </div>
    </div>
  )
}
